import createImageUrlBuilder from '@sanity/image-url';
// import { Link } from '@/sanity.types';
// import { dataset, projectId, studioUrl } from '@/sanity/env';
import { dataset, projectId } from '@/sanity/env';

// import { createDataAttribute, CreateDataAttributeProps } from 'next-sanity';

export const imageBuilder = createImageUrlBuilder({
  projectId: projectId || '',
  dataset: dataset || '',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const urlForImage = (source: any) => {
  // Ensure that source image contains a valid reference
  if (!source?.asset?._ref) {
    return undefined;
  }

  return imageBuilder?.image(source).auto('format').fit('max');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resolveOpenGraphImage(image: any, width = 1200, height = 627) {
  if (!image) return;
  const url = urlForImage(image)?.width(1200).height(627).fit('crop').url();
  if (!url) return;
  return { url, alt: image?.alt as string, width, height };
}

// Depending on the type of link, we need to fetch the corresponding page, post, or URL.  Otherwise return null.
// export function linkResolver(link: Link | undefined) {
//   if (!link) return null;

//   // If linkType is not set but href is, lets set linkType to "href".  This comes into play when pasting links into the portable text editor because a link type is not assumed.
//   if (!link.linkType && link.href) {
//     link.linkType = 'href';
//   }

//   switch (link.linkType) {
//     case 'href':
//       return link.href || null;
//     case 'page':
//       if (link?.page && typeof link.page === 'string') {
//         return `/${link.page}`;
//       }
//     case 'post':
//       if (link?.post && typeof link.post === 'string') {
//         return `/posts/${link.post}`;
//       }
//     default:
//       return null;
//   }
// }

// type DataAttributeConfig = CreateDataAttributeProps &
//   Required<Pick<CreateDataAttributeProps, 'id' | 'type' | 'path'>>;

// export function dataAttr(config: DataAttributeConfig) {
//   return createDataAttribute({
//     projectId,
//     dataset,
//     baseUrl: studioUrl,
//   }).combine(config);
// }

export const isVideoUrl = (url: string | undefined) => {
  if (!url) return false;
  // List of supported video platforms
  const videoPlatforms = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'dailymotion.com',
    'wistia.com',
  ];

  // List of supported video file extensions
  const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi'];

  try {
    const parsedUrl = new URL(url);

    // Check if the URL is from a supported video platform
    const isPlatformUrl = videoPlatforms.some((domain) =>
      parsedUrl.hostname.includes(domain)
    );

    // Check if the URL points to a video file
    const isVideoFile = videoExtensions.some((ext) =>
      parsedUrl.pathname.toLowerCase().endsWith(ext)
    );

    return isPlatformUrl || isVideoFile;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false; // Invalid URL
  }
};

export const isValidUrl = (string: string): boolean => {
  try {
    new URL(string);
    return true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return false;
  }
};

// Sanity CDN base AI URL
const SANITY_CDN_BASE_URL = `https://cdn.sanity.io/files/${projectId}/${dataset}`;

/**
 * Resolves the URL for .ai files based on the asset reference (_ref).
 * @param source - The asset object containing the _ref property.
 * @returns The URL for the .ai file or undefined if the reference is invalid.
 */
export const urlForAiFile = (source: { asset?: { _ref?: string } }) => {
  if (!source?.asset?._ref) {
    return undefined;
  }

  // Extract the file ID and extension from the _ref property
  const ref = source.asset._ref;
  const [fileId, extension] = ref.split('-').slice(1); // Example: "file-abc123-ai"

  if (!fileId || !extension) {
    return undefined; // Return undefined if the _ref format is invalid
  }

  // Construct the URL for the .ai file
  return `${SANITY_CDN_BASE_URL}/${fileId}.${extension}`;
};

/**
 * Resolves metadata for .ai files (URL, alt text, etc.).
 * @param source - The asset object containing the _ref property and optional alt text.
 * @param alt - Optional alt text for the file.
 * @returns An object containing the URL and alt text for the .ai file.
 */
export const resolveAiFileMetadata = (
  source: { asset?: { _ref?: string }; alt?: string },
  alt = 'Adobe Illustrator File'
) => {
  const url = urlForAiFile(source);
  if (!url) return undefined;

  return {
    url,
    alt: source?.alt || alt,
  };
};
